import React, { useState, useCallback } from 'react'
import { useI18n } from '../i18n/index.jsx'
import DashboardLayout from '../components/DashboardLayout'

export default function DocExtension() {
	const { t } = useI18n()
	const [videoUrl, setVideoUrl] = useState('')
	const [localVideoUrl, setLocalVideoUrl] = useState('')

	const toEmbedUrl = useCallback((raw) => {
		if (!raw) return ''
		try {
			const url = new URL(raw)
			if (url.hostname.includes('youtube.com')) {
				const v = url.searchParams.get('v')
				return v ? `https://www.youtube.com/embed/${v}` : raw
			}
			if (url.hostname.includes('youtu.be')) {
				return `https://www.youtube.com/embed/${url.pathname.slice(1)}`
			}
		} catch (e) {
			// not a URL
		}
		return raw
	}, [])

	const onLocalVideo = (e) => {
		const file = e?.target?.files?.[0]
		if (!file) return
		const url = URL.createObjectURL(file)
		setLocalVideoUrl(url)
	}

	const clearLocalVideo = () => {
		if (localVideoUrl) URL.revokeObjectURL(localVideoUrl)
		setLocalVideoUrl('')
	}

	return (
		<DashboardLayout>
			<div className="flex-1 p-8">
				{/* Header */}
				<div className="mb-6">
					<div className="flex items-center gap-3 mb-2">
						<div className="p-2 rounded-lg bg-emerald-500/10">
							<svg className="h-6 w-6 text-emerald-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
								<path d="M12 2L2 7v6c0 6 4 10 10 11s10-5 10-11V7l-10-5z" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</div>
						<div>
							<h1 className="text-3xl font-bold text-white">{t('docext.title')}</h1>
							<p className="text-slate-400 text-sm">{t('docext.subtitle')}</p>
							<div className="mt-3 text-sm">
								<span className="text-slate-400 mr-2">{t('docext.nav.steps')}</span>
								<a href="#step-01" className="text-emerald-300 mr-2">01</a>
								<a href="#step-02" className="text-emerald-300 mr-2">02</a>
								<a href="#step-03" className="text-emerald-300 mr-2">03</a>
								<a href="#step-04" className="text-emerald-300 mr-2">04</a>
								<a href="#faq" className="text-emerald-300">FAQ</a>
							</div>
						</div>
					</div>
				</div>

				{/* Main grid */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
					{/* Left column */}
					<div className="col-span-1 lg:col-span-2 space-y-4">
						<Card>
							<h2 className="text-lg font-semibold text-white mb-2">{t('docext.summary.title')}</h2>
							<p className="text-slate-300">{t('docext.summary.intro')}</p>
							<ol className="list-decimal pl-5 text-slate-300 space-y-2 mt-3">
								<li><a href="#step-01" className="text-emerald-300">{t('docext.summary.install')}</a> {t('docext.summary.step1')}</li>
								<li><a href="#step-02" className="text-emerald-300">{t('docext.summary.retrieve')}</a> {t('docext.summary.step2')}</li>
								<li><a href="#step-03" className="text-emerald-300">{t('docext.summary.link')}</a> {t('docext.summary.step3')}</li>
								<li><a href="#step-04" className="text-emerald-300">{t('docext.summary.verify')}</a> {t('docext.summary.step4')}</li>
							</ol>
						</Card>

						<Card className="mt-4">
							<h2 className="text-lg font-semibold text-white mb-2">{t('docext.detailed.title')}</h2>

							<Step id="step-01" number="01" title={t('docext.step1.title')} desc={<>
								<p className="text-slate-300">1) {t('docext.step1.p1')} <strong>{t('docext.step1.p1.btn')}</strong> {t('docext.step1.p1.end')}</p>
								<p className="text-slate-400 text-sm mt-2">2) {t('docext.step1.p2')}</p>
								<p className="text-slate-400 text-sm mt-2">3) {t('docext.step1.p3')} <code>chrome://extensions</code>{t('docext.step1.p3.mid')} <strong>{t('docext.step1.p3.btn')}</strong> {t('docext.step1.p3.end')}</p>
								<div className="mt-3 text-slate-400 text-sm">{t('docext.step1.note')}</div>
							</>} />

							<Step id="step-02" number="02" title={t('docext.step2.title')} desc={<>
								<p className="text-slate-300">1) {t('docext.step2.p1')} <strong>{t('docext.step2.p1.page')}</strong> {t('docext.step2.p1.end')}</p>
								<p className="text-slate-400 text-sm mt-2">2) {t('docext.step2.p2')} <strong>{t('docext.step2.p2.btn')}</strong>.</p>
								<p className="text-slate-400 text-sm mt-2">3) {t('docext.step2.p3')} <strong>{t('docext.step2.p3.path')}</strong>{t('docext.step2.p3.end')} <strong>{t('docext.step2.p3.page')}</strong> {t('docext.step2.p3.final')}</p>
								<p className="text-slate-400 text-sm mt-2">4) {t('docext.step2.p4')}</p>
								<div className="mt-3 text-slate-400 text-sm">{t('docext.step2.tip')} <strong>{t('docext.step2.tip.page')}</strong> {t('docext.step2.tip.end')}</div>
							</>} />

							<Step id="step-03" number="03" title={t('docext.step3.title')} desc={<>
								<p className="text-slate-300">1) {t('docext.step3.p1')} <strong>{t('docext.step3.p1.opt1')}</strong> {t('docext.step3.p1.or')} <strong>{t('docext.step3.p1.opt2')}</strong>.</p>
								<p className="text-slate-400 text-sm mt-2">2) {t('docext.step3.p2')} <strong>{t('docext.step3.p2.field1')}</strong> {t('docext.step3.p2.or')} <strong>{t('docext.step3.p2.field2')}</strong>{t('docext.step3.p2.end')}</p>
								<p className="text-slate-400 text-sm mt-2">3) {t('docext.step3.p3')} <strong>{t('docext.step3.p3.btn')}</strong> {t('docext.step3.p3.end')}</p>
								<div className="mt-3 text-slate-400 text-sm">{t('docext.step3.note')}</div>
							</>} />

							<Step id="step-04" number="04" title={t('docext.step4.title')} desc={<>
								<p className="text-slate-300">1) {t('docext.step4.p1')}</p>
								<p className="text-slate-400 text-sm mt-2">2) {t('docext.step4.p2')} <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">Ctrl</kbd> + <kbd className="px-2 py-1 bg-slate-800 rounded text-xs">Q</kbd>{t('docext.step4.p2.end')}</p>
							</>} />

						</Card>

						<Card className="mt-4">
							<h2 className="text-lg font-semibold text-white mb-2">{t('docext.security.title')}</h2>
							<ul className="text-slate-300 list-disc pl-5">
								<li><strong>{t('docext.security.web')}</strong> {t('docext.security.web.desc')}</li>
								<li><strong>{t('docext.security.storage')}</strong> {t('docext.security.storage.desc')}</li>
								<li><strong>{t('docext.security.noleak')}</strong> {t('docext.security.noleak.desc')}</li>
							</ul>
							<div className="text-slate-400 text-sm mt-3">{t('docext.security.advice')}</div>
						</Card>

						<Card className="mt-4">
							<h2 className="text-lg font-semibold text-white mb-2">{t('docext.faq.title')}</h2>
							<div id="faq" className="space-y-3 text-slate-300 text-sm">
								<div>
									<div className="font-semibold">{t('docext.faq.q1')}</div>
									<div>{t('docext.faq.a1')} <strong>{t('docext.faq.a1.path')}</strong> {t('docext.faq.a1.end')}</div>
								</div>
								<div>
									<div className="font-semibold">{t('docext.faq.q2')}</div>
									<div>{t('docext.faq.a2')}</div>
								</div>
								<div>
									<div className="font-semibold">{t('docext.faq.q3')}</div>
									<div>{t('docext.faq.a3')}</div>
								</div>
							</div>
						</Card>
					</div>

					{/* Right column (sticky) */}
					<div className="col-span-1">
						<div className="sticky top-8 space-y-4">
							<Card>
								<h3 className="text-md font-semibold text-white mb-2">{t('docext.checklist.title')}</h3>
								<ul className="text-slate-300 list-disc pl-5 space-y-2">
									<li>{t('docext.checklist.1')}</li>
									<li>{t('docext.checklist.2')}</li>
									<li>{t('docext.checklist.3')}</li>
									<li>{t('docext.checklist.4')}</li>
								</ul>
							</Card>
							
							<Card className="mt-4">
								<h3 className="text-md font-semibold text-white mb-2">{t('docext.video.installation.title')}</h3>
								<p className="text-slate-400 text-sm mb-3">{t('docext.video.installation.desc')}</p>
								<div className="mt-3">
									<iframe
										title="Tutoriel Installation Hiperyon"
										src="https://www.youtube.com/embed/QobWMmq9Z54"
										className="w-full h-64 rounded-md border border-white/8 bg-black"
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
									></iframe>
								</div>
							</Card>

							<Card className="mt-4">
								<h3 className="text-md font-semibold text-white mb-2">{t('docext.video.synchronization.title')}</h3>
								<p className="text-slate-400 text-sm mb-3">{t('docext.video.synchronization.desc')}</p>
								<div className="mt-3">
									<iframe
										title="Tutoriel Synchronisation Hiperyon"
										src="https://www.youtube.com/embed/e3sTiK8pguQ"
										className="w-full h-64 rounded-md border border-white/8 bg-black"
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
									></iframe>
								</div>
							</Card>

							<Card className="mt-4">
								<h3 className="text-md font-semibold text-white mb-2">{t('docext.troubleshoot.title')}</h3>
								<ul className="text-slate-300 list-disc pl-5 space-y-2 text-sm">
									<li><strong>{t('docext.troubleshoot.1')}</strong> {t('docext.troubleshoot.1.desc')}</li>
									<li><strong>{t('docext.troubleshoot.2')}</strong> {t('docext.troubleshoot.2.desc')}</li>
									<li><strong>{t('docext.troubleshoot.3')}</strong> {t('docext.troubleshoot.3.desc')}</li>
									<li><strong>{t('docext.troubleshoot.4')}</strong> {t('docext.troubleshoot.4.desc')}</li>
								</ul>
							</Card>


						</div>
					</div>
				</div>
				{/* Contact */}
				<div className="px-8 pb-8">
					<div className="mt-6 p-4 rounded-lg border border-white/6 bg-white/3 text-sm text-slate-300">
						{t('docext.contact')} <a href="mailto:test_contaonc@gmail.com" className="text-emerald-300">test_contaonc@gmail.com</a>
					</div>
				</div>
			</div>
		</DashboardLayout>
	)
}

function Step({ id, number, title, desc }){
	return (
		<div id={id} className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/2">
			<div className="flex items-start gap-4">
				<div className="flex-shrink-0">
					<div className="h-12 w-12 flex items-center justify-center rounded-full bg-emerald-500 text-slate-900 font-bold">{number}</div>
				</div>
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-white">{title}</h3>
					<div className="mt-2 text-slate-400">{desc}</div>
				</div>
			</div>
		</div>
	)
}

function Card({ children, className = '' }){
	return (
		<div className={`p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/2 ${className}`}>
			{children}
		</div>
	)
}
